import { useCallback, useContext, useRef } from 'react';

import { healthCheck } from 'PrivateApis';

import { UserContext } from 'Context/user';

import { useRefreshIdToken } from './useRefreshIdToken';

const DEFAULT_REFRESH_INTERVAL = 86400; // 1 day in secs

const getHealthCheck = () => healthCheck()
  .catch((err) => {
    window?.Sentry?.captureException?.(new Error(err), {
      extra: {
        onLine: globalThis.navigator.onLine,
      },
      level: 'info',
      tags: {
        fetch: 'Health check before cognito refresh',
      },
    });

    return ({ error: err });
  });

export const useSetRefreshToken = () => {
  const { userState } = useContext(UserContext);

  const { attemptTokenRefresh } = useRefreshIdToken();

  const sessionInterval = useRef<ReturnType<typeof setInterval>>(undefined);

  const refreshTokenInterval = (((userState?.cognitoTokens?.expiresIn ?? DEFAULT_REFRESH_INTERVAL) - 60) * 1000);

  const user = Number.isInteger(userState?.id);

  if (user && (!userState?.cognitoTokens?.expiresIn || userState?.cognitoTokens?.expiresIn <= 60)) {
    globalThis?.Sentry?.captureException?.(new Error('Invalid or missing cognito token expiration'), {
      extra: {
        id: user,
      },
      level: 'warning',
      tags: {
        details: 'wrong/missing token expiration',
      },
    });
  }

  const attemptHealthCheckAndTokenRefresh = useCallback(async () => {
    const healthResponse = await getHealthCheck();
    if (!healthResponse?.error) {
      await attemptTokenRefresh();
    }
  }, [attemptTokenRefresh]);

  const cleanupRefreshInterval = useCallback(() => {
    clearInterval(sessionInterval.current);
    sessionInterval.current = undefined;
  }, []);

  const setCognitoIdTokenRefreshInterval = useCallback(async (triggerOnLoad = true) => {
    cleanupRefreshInterval();

    if (triggerOnLoad) {
      await attemptHealthCheckAndTokenRefresh();
    }

    sessionInterval.current = setInterval(() => {
      attemptHealthCheckAndTokenRefresh();
    }, refreshTokenInterval);
  }, [attemptHealthCheckAndTokenRefresh, cleanupRefreshInterval, refreshTokenInterval]);

  const triggerRefreshInterval = useCallback(() =>
    setCognitoIdTokenRefreshInterval(), [setCognitoIdTokenRefreshInterval]);

  return {
    setCognitoIdTokenRefreshInterval,
    stopRefreshInterval: cleanupRefreshInterval,
    triggerRefreshInterval,
  };
};
